import { Decodable } from "./_decodable";
import { Item } from "./item";
import { Client } from "./client";
import { ProposalLog } from "./proposal-log";
import { UserSimple } from "./user";
import { AccountTag } from "./account-tag";
import { AdditionalCharge } from "./additional-charge";
import { DetailsConfigField } from "./details-config";
import { ChosenPayment, PaymentMethodDescribed } from "app/helpers/payment-plan";
import { ItemDetailsDefaults } from "app/helpers/item-details";
import { ProposalLastActivity, ProposalSituation } from "app/helpers/proposal-list";

class Proposal extends Decodable {
    static readonly CREATED_ONLY = 1;
    static readonly SENT = 2;
    static readonly APROVED = 3;
    static readonly REFUSED = 4;
    static readonly BILLED = 5;
    static readonly PAYED = 6;
    static readonly DEFAULT_VALUE = 0;
    static readonly EXPIRED = 7;

    id?: number;
    clientId?: number;
    cotacaoId?: number;
    cotacaoOrigin?: string;
    customerId?: number;
    creatorId?: number;
    accountLayoutProposalId?: number;

    shortDescription?: string;
    termsAndConditions?: string;
    proposalDescription?: string;
    internalNotes?: string;

    installments: number = 1;
    installmentValue: number = 0;

    deadlineDays?: number;
    expiresIn: number = 7;
    daysToExpire?: number;
    dateProposal?: Date;

    discountType: string = "raw";
    discountAmmount: number = 0;
    discountValue: number = 0;

    entryType: string = "raw";
    entryAmmount: number = 0;
    entryValue: number = 0;

    freteValue: number = 0;
    freteInfo?: string;

    juros:number = 0;

    totalLiquid: number = 0;
    totalGross: number = 0;

    canProcess: boolean = false;

    status?: number;
    // Só nos excluídos (a API troca `status` por 'deleted'): status antes da exclusão
    originalStatus?: number;
    statusName?: string;
    statusClass?: string;
    requiresFinancialResetOnUpdate: boolean = false;

    items?: Item[];
    additionalCharges?: AdditionalCharge[];
    tags?: AccountTag[];
    client?: Client;
    logs?: ProposalLog[];

    token: any;
    tokenLink?: string;
    proposalNumber? : number;
    createdAt?: Date;
    updatedAt?: Date;
    hasInstallments?: boolean;
    hideTotalValues?: boolean;
    hideItemValues?: boolean;

    currentStatusDateName?:string
    currentStatusDate?:Date

    customer?:UserSimple

    sentedAt?: Date;
    refusedAt?: Date;
    approvedAt?: Date;
    billedAt?: Date;
    paidAt?: Date;

    refusedReason?: String

    deletedAt?: Date;

    // Configuração de exibição de detalhes dos produtos
    showDetailsSection?: boolean;
    
    // Estrutura de configuração de detalhes (vem do backend)
    // Define quais campos estão disponíveis e seus labels
    detailsConfigStructure?: DetailsConfigField[];

    // ORCA-1931: formas de pagamento. 'legacy' = entrada/parcelas antigas (paymentMethods traz a conversão sugerida)
    paymentFormat?: 'methods' | 'legacy';
    paymentMethods?: PaymentMethodDescribed[];
    paymentMethodsConverted?: boolean;
    paymentToMatch?: boolean;
    chosenPaymentMethod?: string | null;
    chosenInstallments?: number | null;
    chosenPayment?: ChosenPayment | null;

    // ORCA-1937: itens com detalhes. 'legacy' = bloco antigo (itemDetails/itemDetailsModes trazem a conversão)
    itemsFormat?: 'table' | 'legacy';
    itemDetails?: ItemDetailsDefaults;
    itemsDetailsConverted?: boolean;
    itemDetailsModes?: Record<string, 'default' | 'custom' | 'none'>;

    // ORCA-1963: dados da linha da lista de orçamentos
    situation?: ProposalSituation | null;
    daysLeft?: number | null;
    expirationDate?: string | null;
    paymentSummary?: string | null;
    requiresPaymentChoice?: boolean;
    lastActivity?: ProposalLastActivity | null;
    refusedReasonType?: string | null;
    refusedReasonLabel?: string | null;

    // ORCA-1964: token do "Desfazer" devolvido ao aprovar, reprovar, faturar ou registrar pagamento
    undoToken?: string | null;

    protected _map = {
        createdAt: (e) => this.parseDate(e),
        updatedAt: (e) => this.parseDate(e),
        sentedAt: (e) => this.parseDate(e),
        refusedAt: (e) => this.parseDate(e),
        approvedAt: (e) => this.parseDate(e),
        billedAt: (e) => this.parseDate(e),
        paidAt: (e) => this.parseDate(e),

        dateProposal: (e) => this.parseDate(e),
        currentStatusDate: (e) => this.parseDate(e),
        requiresFinancialResetOnUpdate: (e) => !!e,
        showDetailsSection: (e) => !!e, // Converte 1/0 para true/false
        hideTotalValues: (e) => !!e, // Converte 1/0 para true/false
        hideItemValues: (e) => !!e, // Converte 1/0 para true/false
        detailsConfigStructure: (e) => DetailsConfigField.parse(e),
        logs: (e) => ProposalLog.parse(e),
        items: (e) => Item.parse(e),
        additionalCharges: (e) => AdditionalCharge.parse(e),
        tags: (e) => AccountTag.parse(e),
        client: (e) => Client.parse(e),
        customer: (e) => UserSimple.parse(e),
        deletedAt: (e) => this.parseDate(e),
    };
}

export { Proposal };
